import React, { useState, useEffect } from 'react';
import { Skeleton, Card, CardHeader, CardContent, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { pink, green, indigo, lightBlue, cyan, purple, orange } from '@mui/material/colors';
import { AutoStories, FormatListBulleted, Quiz, Comment as CommentIcon, CheckBox, Ballot, SkipNext, SportsScore } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import Grid from '@mui/material/Grid';
import SurveyTabbedHeatmap from './surveys/SurveyTabbedHeatmap';
import SurveyDevices from './surveys/SurveyDevices';
import useApi from '../hooks/useApi';
import useSnackbar from '../hooks/useSnackbar';
import buildUrl from '../buildUrl';
import IconStat from './surveys/IconStat';
import TypedValue from '../TypedValue';

const calculateSurveyMetrics = (surveys, opens, totalOpens) => {
    const metrics = {
        totalSurveys: surveys.length,
        totalPages: 0,
        totalRequired: 0,
        totalComments: 0,
        totalSkipped: 0,
        totalCompleted: 0,
        totalOpened: totalOpens
    };

    const isComment = key => key.toLowerCase().includes('-comment');

    const pageCounts = [];
    const questionCounts = [];
    const questionsPerPageCounts = [];
    const requiredCounts = [];
    const commentCounts = [];
    const skippedCounts = [];
    const completionRates = [];

    const getAllQuestions = survey => {
        const questions = [];

        const extractElements = (elements = []) => {
            for (const e of elements) {
                if (!e || typeof e !== 'object') {
                    continue;
                }

                if (e.type === 'panel' || e.type === 'paneldynamic') {
                    extractElements(e.elements || e.templateElements || []);
                }
                else {
                    questions.push(e);
                }
            }
        };

        for (const page of survey.parsedSurvey?.pages || []) {
            extractElements(page.elements || []);
        }

        return questions;
    };

    for (const survey of surveys) {
        const pages = survey.parsedSurvey?.pages ?? [];
        const results = survey.parsedResults ?? [];
        const surveyOpens = opens.filter(e => e.surveyId === survey.id)?.length ?? 0;

        const pageCount = pages.length;
        const responseCount = results.length;
        const questions = getAllQuestions(survey);

        const questionCount = questions.length;
        const requiredCount = questions.filter(q => q.isRequired).length;
        const questionsPerPage = pageCount ? questionCount / pageCount : 0;
        const completionRate = surveyOpens > 0 ? responseCount / surveyOpens : 0;

        let commentCount = 0;
        let skippedCount = 0;

        for (const response of results) {
            const commentKeys = Object.keys(response).filter(isComment);
            commentCount += commentKeys.length;

            const answeredCount = Object.keys(response).filter(k => !isComment(k)).length;
            skippedCount += Math.max(0, questionCount - answeredCount);
        }

        metrics.totalPages += pageCount;
        metrics.totalCompleted += responseCount;
        metrics.totalRequired += requiredCount;
        metrics.totalComments += commentCount;
        metrics.totalSkipped += skippedCount;

        pageCounts.push(pageCount);
        questionCounts.push(questionCount);
        requiredCounts.push(requiredCount);
        commentCounts.push(commentCount);
        questionsPerPageCounts.push(questionsPerPage);
        skippedCounts.push(skippedCount);
        completionRates.push(completionRate);
    }

    const maybeDivide = (num, denom) => denom ? num / denom : 0;

    const median = arr => {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);

        return sorted.length % 2 === 0
            ? (sorted[mid - 1] + sorted[mid]) / 2
            : sorted[mid];
    };

    return {
        meanQuestionsPerSurvey: maybeDivide(questionCounts.reduce((a, b) => a + b, 0), metrics.totalSurveys),
        medianQuestionsPerSurvey: median(questionCounts),

        meanPagesPerSurvey: maybeDivide(metrics.totalPages, metrics.totalSurveys),
        medianPagesPerSurvey: median(pageCounts),

        meanRequiredQuestionsPerSurvey: maybeDivide(metrics.totalRequired, metrics.totalSurveys),
        medianRequiredQuestionsPerSurvey: median(requiredCounts),

        meanCommentsPerSurvey: maybeDivide(metrics.totalComments, metrics.totalSurveys),
        medianCommentsPerSurvey: median(commentCounts),

        meanQuestionsPerPagePerSurvey: maybeDivide(questionsPerPageCounts.reduce((a, b) => a + b, 0), metrics.totalSurveys),
        medianQuestionsPerPagePerSurvey: median(questionsPerPageCounts),

        meanSkippedQuestionsPerSurvey: maybeDivide(metrics.totalSkipped, metrics.totalSurveys),
        medianSkippedQuestionsPerSurvey: median(skippedCounts),

        meanCompletionRate: maybeDivide(completionRates.reduce((a, b) => a + b, 0), metrics.totalSurveys),
        medianCompletionRate: median(completionRates)
    };
};

const SurveysDashboard = ({ data }) => {
    const { handleGet } = useApi();
    const { showSnackbar } = useSnackbar();
    const theme = useTheme();
    const textColour = theme.palette.common.white;
    const [actions, setActions] = useState(null);
    const [devices, setDevices] = useState(null);
    const [surveysData, setSurveysData] = useState(null);
    const [surveysMetrics, setSurveysMetrics] = useState(null);
    const [averageType, setAverageType] = useState('Mean');

    const isMean = averageType === 'Mean';
    const surveyCount = data?.length ?? 0;

    useEffect(() => {
        if (data) {
            const parsedData = data.map(({ id, json, results }) => {
                const parsedSurvey = JSON.parse(json);
                const parsedResults = results.map(e => JSON.parse(e.result));

                return {
                    id,
                    parsedSurvey,
                    parsedResults
                };
            });

            setSurveysData(parsedData);
        }
    }, [data]);

    useEffect(() => {
        if (surveysData && actions) {
            const opens = actions.filter(e => e.actionType === 'Survey Open');

            const totalOpens = opens?.length ?? 0;

            const surveysMetrics = calculateSurveyMetrics(surveysData, opens, totalOpens);

            setSurveysMetrics(surveysMetrics);
        }
    }, [surveysData, actions]);

    useEffect(() => {
        if (actions) {
            const allDevices = ['Desktop', 'Mobile', 'Tablet'];

            const devices = allDevices.map(device => ({
                name: device,
                value: actions.reduce((acc, item) => item.device === device ? acc + 1 : acc, 0)
            }));

            setDevices(devices);
        }
    }, [actions]);

    const handleFetchActions = async () => {
        const ids = data.map(e => e.id);

        const params = { ids };

        const url = buildUrl('surveys/reports/actions', params);

        const response = await handleGet(url);

        if (!response.ok) {
            showSnackbar('Error fetching Actions', 'error');
            return;
        }

        setActions(await response.json());
    };

    useEffect(() => {
        data && handleFetchActions();
    }, [data]);

    if (!surveysMetrics) {
        return (
            <Grid container spacing={2}>
                <Grid size={12}>
                    <Skeleton height={150} />
                </Grid>
                <Grid size={{ xs: 12, md: 9 }}>
                    <Skeleton height={300} />
                </Grid>
                <Grid size={{ xs: 12, md: 3 }}>
                    <Skeleton height={300} />
                </Grid>
            </Grid>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Card sx={{ height: '100%', pb: 1 }}>
                    <CardHeader
                        sx={{
                            m: 0,
                            py: 1,
                            '& .MuiCardHeader-action': {
                                alignSelf: 'center'
                            }
                        }}
                        slotProps={{
                            title: {
                                variant: 'h6',
                                fontWeight: 'normal'
                            }
                        }}
                        avatar={<FormatListBulleted />}
                        title={`${surveyCount} Survey${surveyCount === 1 ? '' : 's'}: Quick Takeaways`}
                        action={(
                            <ToggleButtonGroup
                                color="primary"
                                value={averageType}
                                exclusive
                                onChange={(e, type) => setAverageType(type)}
                                aria-label={`${averageType} Average`}
                                size="small"
                            >
                                <ToggleButton value="Mean">Mean</ToggleButton>
                                <ToggleButton value="Median">Median</ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    />
                    <CardContent
                        sx={{
                            m: 0,
                            px: 1,
                            py: 0,
                            '&:last-child': {
                                paddingBottom: 0
                            }
                        }}
                    >
                        <Grid container spacing={1}>
                            <Grid size={{ xs: 12, md: 10 }}>
                                <Box>
                                    <Grid container spacing={1}>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <Quiz
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanQuestionsPerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianQuestionsPerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Questions`}
                                                colour={indigo[500]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <AutoStories
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanPagesPerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianPagesPerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Pages`}
                                                colour={purple[500]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <Ballot
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanQuestionsPerPagePerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianQuestionsPerPagePerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Questions Per Page`}
                                                colour={pink[500]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <CheckBox
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanRequiredQuestionsPerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianRequiredQuestionsPerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Required Questions`}
                                                colour={cyan[600]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <CommentIcon
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanCommentsPerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianCommentsPerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Comments`}
                                                colour={lightBlue[500]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                        <Grid size={{ xs: 4, md: 2 }}>
                                            <IconStat
                                                icon={(
                                                    <SkipNext
                                                        fontSize="large"
                                                        sx={{ color: textColour }}
                                                    />
                                                )}
                                                figure={isMean ? <TypedValue value={surveysMetrics.meanSkippedQuestionsPerSurvey} type="number" accuracy={2} /> : <TypedValue value={surveysMetrics.medianSkippedQuestionsPerSurvey} type="number" accuracy={0} />}
                                                text={`${averageType} Skipped Questions`}
                                                colour={orange[500]}
                                                textColour={textColour}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 2 }}>
                                <IconStat
                                    icon={(
                                        <SportsScore
                                            fontSize="large"
                                            sx={{ color: textColour }}
                                        />
                                    )}
                                    figure={isMean ? <TypedValue value={surveysMetrics.meanCompletionRate === 0 ? surveysMetrics.meanCompletionRate : (surveysMetrics.meanCompletionRate * 100)} type="rate" accuracy={1} /> : <TypedValue value={surveysMetrics.medianCompletionRate === 0 ? surveysMetrics.medianCompletionRate : (surveysMetrics.medianCompletionRate * 100)} type="rate" accuracy={1} />}
                                    text={`${averageType} Completion Rate`}
                                    colour={green[600]}
                                    textColour={textColour}
                                />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 9 }}>
                <SurveyTabbedHeatmap actions={actions} />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
                <SurveyDevices
                    data={devices}
                    height="100%"
                />
            </Grid>
        </Grid>
    );
};

export default SurveysDashboard;